import { useState, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * useDrawerToggler
 *
 * Custom hook to manage the state and behavior of a responsive navigation drawer.
 * Handles open/close state, drawer width, and adapts to mobile/desktop breakpoints.
 *
 * @returns {
 *   drawerOpen: boolean - Whether the drawer is open
 *   drawerWidth: number - Current width of the drawer
 *   isMobile: boolean - True if the viewport is mobile size
 *   toggleDrawer: () => void - Function to toggle the drawer open/closed
 *   handleWidthChange: (width: number) => void - Function to set the drawer width
 * }
 */
export const useDrawerToggler = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(240);
    const [drawerReady, setDrawerReady] = useState(false);

    const toggleDrawer = useCallback(() => {
        setDrawerOpen((prev) => !prev);
    }, [drawerOpen]);

    const handleWidthChange = useCallback((width: number) => {
        setDrawerWidth(width);
        if (!drawerReady) {
            setDrawerReady(true);
        }
    }, []);

    return {
        drawerOpen,
        drawerWidth,
        isMobile,
        drawerReady,
        toggleDrawer,
        handleWidthChange,
    };
};
